import { IS_PROD_BUILD } from '@src/config/env'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

export const DESIGN_SYSTEM_GATE_NAME = 'ssr-use-design-system-components'
export const DESIGN_SYSTEM_QUERY_PARAM_KEY = 'is_ds'

export const DOCUMENT_HTML_DATA_STYLES_ATTR = 'data-styles'
export const DOCUMENT_HTML_DATA_STYLES_VALUE = 'panda'

/**
 * Whether the `is_ds` query-param / Next-data override for the design system is
 * allowed. It should work in every non-production context — local dev, tests,
 * and non-prod deployments (stage / qa / preview) — but never in a production
 * build.
 *
 * `IS_PROD_BUILD` alone is insufficient: local dev and the test runner leave
 * `NEXT_PUBLIC_DEPLOY_ENVIRONMENT` unset, so it defaults to `true` there
 * (`src/config/env.ts`). The `NODE_ENV` check keeps the override working in
 * those contexts, while `!IS_PROD_BUILD` extends it to non-prod deployments
 * (where `NODE_ENV === 'production'` but the build env is not prod).
 */
export function isDesignSystemPreviewAllowed(): boolean {
  return process.env.NODE_ENV !== 'production' || !IS_PROD_BUILD
}

export function shouldUseDesignSystem(): boolean {
  if (isBrowser()) {
    return document.documentElement.getAttribute(DOCUMENT_HTML_DATA_STYLES_ATTR) === DOCUMENT_HTML_DATA_STYLES_VALUE
  }

  const featureGates = getUserAgnosticGlobalFeatureGatesFromMemory()
  return Boolean(featureGates[DESIGN_SYSTEM_GATE_NAME])
}

export function isDesignSystemEnabledFromNextData(nextData: {
  props?: { pageProps?: { userAgnosticGlobalFeatureGates?: Record<string, boolean | undefined> } }
  query?: Record<string, string | string[] | undefined>
}): boolean {
  // The Statsig gate is authoritative in EVERY environment, including
  // production. This is the same gate `shouldUseDesignSystem()` reads
  // server-side, and this function is the sole writer of `data-styles="panda"`
  // on `<html>` (`_document.page.tsx`). If the gate check were placed behind the
  // (prod-disabled) preview guard, production SSR would render DS while
  // `_document` left the attribute unset → client reads legacy → hydration
  // mismatch, and DS could never ship to prod traffic via the gate.
  if (nextData.props?.pageProps?.userAgnosticGlobalFeatureGates?.[DESIGN_SYSTEM_GATE_NAME]) {
    return true
  }

  // The `is_ds` query-param override is a dev/QA affordance only — honored in
  // non-production contexts (local dev, tests, stage/qa/preview deployments),
  // never in a production build.
  if (isDesignSystemPreviewAllowed()) {
    const queryValue = nextData.query?.[DESIGN_SYSTEM_QUERY_PARAM_KEY]
    return (Array.isArray(queryValue) ? queryValue[0] : queryValue) === 'true'
  }

  return false
}
